/**
 * Community Event Create Page
 * /community/groups/:groupId/events/new - 일정 생성 페이지
 */

import { useState } from "react";
import { useLocation, useRoute } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ArrowLeft, Save, Calendar, MapPin, Users, Repeat } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { PageContainer } from "@/components/layout/PageContainer";
import Seo from "@/components/seo/Seo";
import { useAuth } from "@/contexts/AuthContext";
import { buildLoginPath } from "@/lib/auth-navigation";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";

const formSchema = z.object({
  title: z.string().min(1, "제목을 입력해주세요").max(200),
  description: z.string().max(2000).optional(),
  startDate: z.string().min(1, "시작 날짜를 선택해주세요"),
  startTime: z.string().min(1, "시작 시간을 선택해주세요"),
  endDate: z.string().min(1, "종료 날짜를 선택해주세요"),
  endTime: z.string().min(1, "종료 시간을 선택해주세요"),
  locationName: z.string().max(200).optional(),
  locationAddress: z.string().max(500).optional(),
  capacity: z.number().int().positive().optional().or(z.literal("")),
  isRecurring: z.boolean().default(false),
  recurrenceType: z.enum(["weekly", "biweekly", "monthly"]).optional(),
  recurrenceEndDate: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

interface Group {
  id: number;
  title: string;
  slug: string;
}

export default function EventCreatePage() {
  const { user } = useAuth();
  const [, navigate] = useLocation();
  const [match, params] = useRoute<{ groupId: string }>("/community/groups/:groupId/events/new");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const groupId = params?.groupId ? parseInt(params.groupId) : null;

  // 그룹 상세 조회
  const { data: group, isLoading: groupLoading } = useQuery<Group>({
    queryKey: ["/api/community/groups", groupId],
    queryFn: async () => {
      if (!groupId) throw new Error("Invalid group ID");
      const response = await fetch(`/api/community/groups/${groupId}`);
      if (!response.ok) throw new Error("Failed to fetch group");
      return response.json();
    },
    enabled: !!groupId,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      isRecurring: false,
    },
  });

  const isRecurring = watch("isRecurring");
  const recurrenceType = watch("recurrenceType");

  // 일정 생성
  const createEventMutation = useMutation({
    mutationFn: async (data: FormData) => {
      if (!user || !groupId) throw new Error("로그인이 필요합니다");

      // 날짜/시간을 Unix timestamp로 변환
      const startDateTime = new Date(`${data.startDate}T${data.startTime}`);
      const endDateTime = new Date(`${data.endDate}T${data.endTime}`);
      const startTs = Math.floor(startDateTime.getTime() / 1000);
      const endTs = Math.floor(endDateTime.getTime() / 1000);

      if (startTs >= endTs) {
        throw new Error("종료 시간은 시작 시간보다 늦어야 합니다");
      }

      // rrule 생성 (간단한 구현)
      let rrule: string | null = null;
      if (data.isRecurring && data.recurrenceType) {
        const endDate = data.recurrenceEndDate
          ? new Date(data.recurrenceEndDate)
          : null;
        const until = endDate ? endDate.toISOString().split("T")[0].replace(/-/g, "") : null;

        switch (data.recurrenceType) {
          case "weekly":
            rrule = `FREQ=WEEKLY${until ? `;UNTIL=${until}` : ""}`;
            break;
          case "biweekly":
            rrule = `FREQ=WEEKLY;INTERVAL=2${until ? `;UNTIL=${until}` : ""}`;
            break;
          case "monthly":
            rrule = `FREQ=MONTHLY${until ? `;UNTIL=${until}` : ""}`;
            break;
        }
      }

      const token = await user.getIdToken();
      const requestBody = {
        title: data.title,
        description: data.description || null,
        startTs: startTs.toString(),
        endTs: endTs.toString(),
        timezone: "Australia/Sydney",
        locationName: data.locationName || null,
        locationAddress: data.locationAddress || null,
        capacity: data.capacity === "" ? null : data.capacity,
        isRecurring: data.isRecurring,
        rrule: rrule,
      };

      const response = await fetch(`/api/community/groups/${groupId}/events`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "일정 생성에 실패했습니다");
      }

      return response.json();
    },
    onSuccess: (event) => {
      queryClient.invalidateQueries({ queryKey: ["/api/community/groups", groupId, "events"] });
      queryClient.invalidateQueries({ queryKey: ["/api/community/groups", groupId] });
      toast({
        title: "일정이 생성되었습니다",
        description: "일정 상세 페이지로 이동합니다.",
      });
      navigate(`/community/events/${event.id}`);
    },
    onError: (error: Error) => {
      toast({
        title: "오류",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: FormData) => {
    createEventMutation.mutate(data);
  };

  if (!match || !params || !groupId) {
    return (
      <PageContainer size="read">
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">유효하지 않은 모임 ID입니다</p>
            <Button className="mt-4" onClick={() => navigate("/community/groups")}>
              목록으로
            </Button>
          </CardContent>
        </Card>
      </PageContainer>
    );
  }

  if (groupLoading) {
    return (
      <PageContainer size="read">
        <div className="flex items-center justify-center py-12">
          <p className="text-muted-foreground">로딩 중...</p>
        </div>
      </PageContainer>
    );
  }

  if (!group) {
    return (
      <PageContainer size="read">
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">모임을 찾을 수 없습니다</p>
            <Button className="mt-4" onClick={() => navigate("/community/groups")}>
              목록으로
            </Button>
          </CardContent>
        </Card>
      </PageContainer>
    );
  }

  if (!user) {
    return (
      <PageContainer size="read">
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">로그인이 필요합니다</p>
            <Button className="mt-4" onClick={() => navigate(buildLoginPath())}>
              로그인하기
            </Button>
          </CardContent>
        </Card>
      </PageContainer>
    );
  }

  // 기본 날짜/시간 설정 (오늘, 1시간 후)
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const defaultStartDate = today.toISOString().split("T")[0];
  const defaultEndDate = tomorrow.toISOString().split("T")[0];
  const defaultStartTime = "19:00";
  const defaultEndTime = "21:00";

  return (
    <>
      <Seo
        title={`${group.title} - 일정 생성`}
        description="모임 일정을 생성해주세요"
        path={`/community/groups/${groupId}/events/new`}
      />
      <PageContainer size="read">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(`/community/groups/${groupId}`)}
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">일정 생성</h1>
              <p className="text-muted-foreground">{group.title}</p>
            </div>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>일정 생성</CardTitle>
              <CardDescription>모임 일정을 생성해주세요</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {/* 제목 */}
                <div className="space-y-2">
                  <Label htmlFor="title">제목 *</Label>
                  <Input
                    id="title"
                    {...register("title")}
                    placeholder="예: 정기 모임"
                  />
                  {errors.title && (
                    <p className="text-sm text-destructive">{errors.title.message}</p>
                  )}
                </div>

                {/* 설명 */}
                <div className="space-y-2">
                  <Label htmlFor="description">설명 (선택)</Label>
                  <Textarea
                    id="description"
                    {...register("description")}
                    placeholder="일정에 대한 설명을 입력해주세요"
                    rows={4}
                  />
                </div>

                {/* 시작 날짜/시간 */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="startDate">시작 날짜 *</Label>
                    <Input
                      id="startDate"
                      type="date"
                      {...register("startDate")}
                      defaultValue={defaultStartDate}
                    />
                    {errors.startDate && (
                      <p className="text-sm text-destructive">{errors.startDate.message}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="startTime">시작 시간 *</Label>
                    <Input
                      id="startTime"
                      type="time"
                      {...register("startTime")}
                      defaultValue={defaultStartTime}
                    />
                    {errors.startTime && (
                      <p className="text-sm text-destructive">{errors.startTime.message}</p>
                    )}
                  </div>
                </div>

                {/* 종료 날짜/시간 */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="endDate">종료 날짜 *</Label>
                    <Input
                      id="endDate"
                      type="date"
                      {...register("endDate")}
                      defaultValue={defaultEndDate}
                    />
                    {errors.endDate && (
                      <p className="text-sm text-destructive">{errors.endDate.message}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="endTime">종료 시간 *</Label>
                    <Input
                      id="endTime"
                      type="time"
                      {...register("endTime")}
                      defaultValue={defaultEndTime}
                    />
                    {errors.endTime && (
                      <p className="text-sm text-destructive">{errors.endTime.message}</p>
                    )}
                  </div>
                </div>

                {/* 반복 일정 */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between space-x-2">
                    <div className="space-y-0.5">
                      <Label htmlFor="isRecurring">반복 일정</Label>
                      <p className="text-sm text-muted-foreground">
                        체크하면 정기적으로 반복되는 일정입니다
                      </p>
                    </div>
                    <Switch
                      id="isRecurring"
                      checked={isRecurring}
                      onCheckedChange={(checked) => setValue("isRecurring", checked)}
                    />
                  </div>

                  {isRecurring && (
                    <div className="space-y-4 pl-4 border-l-2 border-muted">
                      <div className="space-y-2">
                        <Label htmlFor="recurrenceType">반복 주기 *</Label>
                        <Select
                          value={recurrenceType || ""}
                          onValueChange={(value) =>
                            setValue("recurrenceType", value as "weekly" | "biweekly" | "monthly")
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="반복 주기를 선택해주세요" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="weekly">매주</SelectItem>
                            <SelectItem value="biweekly">격주</SelectItem>
                            <SelectItem value="monthly">매월</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="recurrenceEndDate">반복 종료일 (선택)</Label>
                        <Input
                          id="recurrenceEndDate"
                          type="date"
                          {...register("recurrenceEndDate")}
                        />
                        <p className="text-sm text-muted-foreground">
                          비워두면 무기한 반복됩니다
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* 장소 */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="locationName">장소 이름 (선택)</Label>
                    <Input
                      id="locationName"
                      {...register("locationName")}
                      placeholder="예: Strathfield Library"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="locationAddress">장소 주소 (선택)</Label>
                    <Input
                      id="locationAddress"
                      {...register("locationAddress")}
                      placeholder="예: 65-67 Rochester St, Homebush NSW 2140"
                    />
                  </div>
                </div>

                {/* 최대 인원 */}
                <div className="space-y-2">
                  <Label htmlFor="capacity">최대 인원 (선택)</Label>
                  <Input
                    id="capacity"
                    type="number"
                    {...register("capacity", {
                      setValueAs: (v) => (v === "" ? "" : parseInt(v, 10)),
                    })}
                    placeholder="예: 20"
                    min={1}
                  />
                  <p className="text-sm text-muted-foreground">
                    비워두면 인원 제한이 없습니다
                  </p>
                </div>

                {/* 저장 버튼 */}
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate(`/community/groups/${groupId}`)}
                  >
                    취소
                  </Button>
                  <Button type="submit" disabled={createEventMutation.isPending}>
                    <Save className="w-4 h-4 mr-2" />
                    {createEventMutation.isPending ? "생성 중..." : "생성하기"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </PageContainer>
    </>
  );
}
